(function () {
  'use strict';
  const VERSION = '2026-10-10-v2.1';
  if (window.BMGExperienceV2?.version === VERSION) { window.BMGExperienceV2.init(); return; }
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  const instances = new Map();
  const clamp = value => Math.max(0, Math.min(1, value));
  const mix = (a, b, p) => a + (b - a) * p;
  const smooth = (a, b, value) => { const p = clamp((value - a) / (b - a)); return p * p * (3 - 2 * p); };
  const read = key => { try { return JSON.parse(sessionStorage.getItem(key) || 'null'); } catch (_) { return null; } };
  const store = (key, value) => { try { sessionStorage.setItem(key, JSON.stringify(value)); } catch (_) {} };
  const remove = key => { try { sessionStorage.removeItem(key); } catch (_) {} };
  const configuration = () => document.querySelector('[data-bmg-v2-config]');
  const motion = () => !preference.matches && configuration()?.dataset.motion !== 'false';
  const plainClick = event => event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
  function keyForURL(value) {
    try { const url = new URL(value, location.href); url.searchParams.delete('preview_theme_id'); url.hash = ''; return url.origin + url.pathname + url.search; } catch (_) { return ''; }
  }
  function lastGallery(handle) {
    const saved = read('bmg-v2-gallery');
    if (!saved || Date.now() - saved.time > 3600000 || (handle && saved.handle !== handle)) return null;
    try { const url = new URL(saved.url); if (url.origin !== location.origin || !url.pathname.includes('/collections/')) return null; } catch (_) { return null; }
    return saved;
  }
  function mount(root, setup) {
    if (!root || instances.has(root)) return;
    const abort = new AbortController();
    const cleanups = [];
    const state = {
      listen: (target, name, callback, options = {}) => target?.addEventListener(name, callback, {...options, signal: abort.signal}),
      cleanup: callback => cleanups.push(callback),
      animate: (target, frames, options) => {
        if (!target?.animate || !motion()) return;
        const animation = target.animate(frames, options);
        cleanups.push(() => animation.cancel());
        return animation;
      }
    };
    const dispose = () => { abort.abort(); cleanups.reverse().forEach(fn => { try { fn(); } catch (_) {} }); instances.delete(root); };
    instances.set(root, dispose);
    try { setup(root, state); } catch (error) { dispose(); console.warn('BMG gallery enhancement skipped:', error.message); }
  }
  function reveal(elements, state) {
    if (!motion() || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(entries => entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      state.animate(entry.target, [{opacity:.25, transform:'translate3d(0,14px,0)'}, {opacity:1, transform:'translate3d(0,0,0)'}], {duration:560, easing:'cubic-bezier(.22,.75,.2,1)'});
    }), {threshold:.06, rootMargin:'0px 0px -3% 0px'});
    elements.forEach(element => {
      element.removeAttribute('data-bmg-reveal');
      const rect = element.getBoundingClientRect();
      if (rect.top > innerHeight - 30 && rect.height > 0) observer.observe(element);
    });
    state.cleanup(() => observer.disconnect());
  }
  function homeSetup(root, state) {
    reveal([...root.querySelectorAll('[data-bmg-reveal]')].filter(el => !el.closest('.bmg-home__hero')), state);
    const hero = root.querySelector('[data-bmg-hero]');
    const header = document.querySelector('.bmg-site-header');
    let frame = 0;
    const updateHeader = () => { frame = 0; header?.classList.toggle('is-v2-solid', scrollY > Math.max(90, (hero?.offsetHeight || 0) - 84)); };
    const tick = () => { if (!frame) frame = requestAnimationFrame(updateHeader); };
    state.listen(window, 'scroll', tick, {passive:true});
    state.listen(window, 'resize', tick, {passive:true});
    state.cleanup(() => { cancelAnimationFrame(frame); header?.classList.remove('is-v2-solid'); });
    updateHeader();
    const navigation = performance.getEntriesByType('navigation')[0];
    if (hero && scrollY < 80 && navigation?.type !== 'back_forward' && !read('bmg-v2-arrived') && motion()) {
      store('bmg-v2-arrived', true);
      state.animate(hero.querySelector('.bmg-home__hero-media'), [{transform:'scale(1.035)'},{transform:'scale(1)'}], {duration:1100,easing:'cubic-bezier(.2,.65,.25,1)'});
      state.animate(hero.querySelector('h1'), [{opacity:.65,transform:'translate3d(0,14px,0)'},{opacity:1,transform:'translate3d(0,0,0)'}], {duration:650,easing:'cubic-bezier(.22,.75,.2,1)'});
    }
  }
  function journeySetup(root, state) {
    const track = root.querySelector('[data-v2-track]');
    const stage = root.querySelector('[data-v2-stage]');
    const world = root.querySelector('[data-v2-world]');
    const art = root.querySelector('[data-v2-art]');
    const room = root.querySelector('.bmg-v2-journey__room');
    const mat = root.querySelector('[data-v2-mat]');
    const shadow = root.querySelector('[data-v2-shadow]');
    const edges = ['top','right','bottom','left'].map(side => root.querySelector('[data-v2-edge="' + side + '"]'));
    const steps = [...root.querySelectorAll('[data-v2-step]')];
    const ratio = Math.max(.15, Number(root.dataset.artRatio) || 3);
    if (!track || !world || !art || !room || !motion()) return;
    let dimensions, active = true, scheduled = 0, phase = -1, failed = false;
    function reset() {
      root.removeAttribute('data-v2-running');
      [world, art, room, mat, shadow, ...edges].forEach(el => el?.removeAttribute('style'));
      ['--v2-stage-height','--v2-edge','--v2-mat'].forEach(name => root.style.removeProperty(name));
      steps.forEach(el => el.removeAttribute('aria-current'));
    }
    function measure() {
      if (failed) return;
      const width = stage.clientWidth;
      if (!width) return;
      const mobile = width <= 700;
      const height = mobile ? Math.max(400, Math.min(innerHeight - 70, width * 1.3)) : Math.max(440, innerHeight - 64);
      const worldWidth = mobile ? width * 1.4 : Math.max(width, height * 1.6);
      const worldHeight = worldWidth / 1.6;
      const artWidth = Math.min(worldWidth * .42, worldHeight * .34 * ratio);
      const artHeight = artWidth / ratio;
      const startWidth = Math.min(width * .96, (height - 172) * ratio);
      dimensions = {width,height,worldWidth,worldHeight,artWidth,artX:worldWidth*.52,artY:worldHeight*.51-artHeight/2,start:startWidth/artWidth,mobile};
      root.style.setProperty('--v2-stage-height', height + 'px');
      root.style.setProperty('--v2-edge', Math.max(3, Math.min(11, worldWidth*.0055)) + 'px');
      root.style.setProperty('--v2-mat', Math.max(4, Math.min(18, worldWidth*.009)) + 'px');
      world.style.width = worldWidth + 'px';
      world.style.height = worldHeight + 'px';
      art.style.width = artWidth + 'px';
      root.setAttribute('data-v2-running', '');
      render();
    }
    function render() {
      scheduled = 0;
      if (!dimensions || failed) return;
      const d = dimensions;
      const p = clamp((64 - track.getBoundingClientRect().top) / Math.max(1, track.offsetHeight - d.height));
      const roomProgress = smooth(.55,.95,p);
      const framedScale = mix(d.start,d.start*.79,smooth(.05,.24,p));
      const scale = mix(framedScale,1,roomProgress);
      const x = mix(d.width/2 - d.artX*scale,(d.width-d.worldWidth)/2,roomProgress);
      const y = mix(d.height*(d.mobile ? .42 : .47)-d.artY*scale,(d.height-d.worldHeight)/2,roomProgress);
      world.style.transform = 'translate3d(' + x.toFixed(2) + 'px,' + y.toFixed(2) + 'px,0) scale(' + scale.toFixed(5) + ')';
      room.style.opacity = smooth(.54,.75,p).toFixed(4);
      mat.style.opacity = smooth(.15,.32,p).toFixed(4);
      shadow.style.opacity = smooth(.43,.6,p).toFixed(4);
      edges.forEach((edge,index) => {
        const value = smooth(.19+index*.06,.34+index*.06,p);
        edge.style.transform = (index%2 ? 'scaleY(' : 'scaleX(') + value.toFixed(5) + ')';
        edge.style.opacity = value > 0 ? '1' : '0';
      });
      const nextPhase = p < .19 ? 0 : p < .55 ? 1 : 2;
      if (phase !== nextPhase) { phase = nextPhase; steps.forEach((step,index) => { if(index===phase)step.setAttribute('aria-current','step');else step.removeAttribute('aria-current'); }); }
    }
    const tick = () => { if (active && !failed && !scheduled) scheduled = requestAnimationFrame(render); };
    state.listen(window, 'scroll', tick, {passive:true});
    let resizeFrame = 0;
    const resize = () => { if (!resizeFrame) resizeFrame = requestAnimationFrame(() => { resizeFrame = 0; measure(); }); };
    state.listen(window, 'resize', resize, {passive:true});
    if ('ResizeObserver' in window) {
      let lastWidth = -1;
      const observer = new ResizeObserver(entries => { const width = entries[0]?.contentRect.width; if (width && Math.abs(width-lastWidth)>1) { lastWidth=width; resize(); } });
      observer.observe(stage); state.cleanup(() => observer.disconnect());
    }
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => { active=entries.some(entry=>entry.isIntersecting); if(active)tick(); }, {rootMargin:'100% 0px'});
      observer.observe(track); state.cleanup(() => observer.disconnect());
    }
    state.listen(room, 'error', () => { failed=true; reset(); root.setAttribute('data-v2-room-unavailable',''); });
    state.listen(room, 'load', tick);
    state.cleanup(() => { cancelAnimationFrame(scheduled);cancelAnimationFrame(resizeFrame);reset(); });
    measure();
  }
  function catalogueSetup(root, state) {
    const grid = root.querySelector('.bmg-print-shop__grid');
    const template = document.querySelector('#bmg-v2-gallery-toolbar');
    if (!grid || !template) return;
    const toolbar = template.content.firstElementChild.cloneNode(true);
    grid.before(toolbar);
    const buttons = [...toolbar.querySelectorAll('[data-bmg-v2-view]')];
    const preferenceKey = 'bmg-v2-view:' + location.pathname;
    let view = read(preferenceKey);
    if (!['gallery','grid'].includes(view)) view = root.dataset.bmgBrowseCategory === 'latest-release' ? 'gallery' : 'grid';
    let scheduled = 0;
    const seen = new WeakSet();
    const observer = motion() && 'IntersectionObserver' in window ? new IntersectionObserver(entries => entries.forEach(entry=>{
      if (!entry.isIntersecting || entry.target.hidden) return;
      observer.unobserve(entry.target);
      if(seen.has(entry.target))return;
      seen.add(entry.target);
      state.animate(entry.target.querySelector('.bmg-print-shop__media'),[{opacity:.3,transform:'translate3d(0,12px,0)'},{opacity:1,transform:'translate3d(0,0,0)'}],{duration:520,easing:'cubic-bezier(.22,.75,.2,1)'});
    }),{threshold:.04}) : null;
    function layout() {
      scheduled = 0;
      const cards = [...grid.querySelectorAll('.bmg-print-shop__card')];
      const visible = cards.filter(card => !card.hidden && card.style.display !== 'none');
      root.dataset.v2View = view;
      buttons.forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.bmgV2View===view)));
      visible.forEach(card=>{
        const image=card.querySelector('.bmg-print-shop__image');
        const width=Number(image?.getAttribute('width'))||image?.naturalWidth||3;
        const height=Number(image?.getAttribute('height'))||image?.naturalHeight||2;
        const ratio=width/Math.max(height,1);
        card.dataset.v2Format=ratio>=2.3?'panorama':ratio<1?'portrait':'landscape';
        if(observer&&!seen.has(card)) {
          if(card.getBoundingClientRect().top<innerHeight-10)seen.add(card);else observer.observe(card);
        }
      });
      for(let index=0;index<visible.length;index++) {
        const card=visible[index];
        if(card.dataset.v2Format==='panorama'){card.style.setProperty('--v2-column','1 / -1');continue;}
        const next=visible[index+1];
        if(!next||next.dataset.v2Format==='panorama') {
          card.style.setProperty('--v2-column',card.dataset.v2Format==='portrait'?'5 / span 4':'3 / span 8');continue;
        }
        const firstPortrait=card.dataset.v2Format==='portrait';
        const secondPortrait=next.dataset.v2Format==='portrait';
        const first=firstPortrait===secondPortrait?6:firstPortrait?5:7;
        card.style.setProperty('--v2-column','span '+first);
        next.style.setProperty('--v2-column','span '+(12-first));
        index++;
      }
      const saved=lastGallery();
      if(saved&&keyForURL(saved.url)===keyForURL(location.href)) {
        const card=visible.find(item=>item.dataset.handle===saved.handle);
        const returningImage=card?.querySelector('.bmg-print-shop__image');
        if(returningImage)returningImage.style.viewTransitionName='bmg-focus-work';
      }
    }
    const tick=()=>{if(!scheduled)scheduled=requestAnimationFrame(layout);};
    state.listen(toolbar,'click',event=>{
      const button=event.target.closest('[data-bmg-v2-view]');if(!button)return;
      view=button.dataset.bmgV2View;store(preferenceKey,view);layout();
    });
    const mutations=new MutationObserver(tick);
    mutations.observe(grid,{childList:true,subtree:true,attributes:true,attributeFilter:['hidden']});
    state.listen(grid,'load',tick,{capture:true});
    state.listen(grid,'click',event=>{
      const anchor=event.target.closest('.bmg-print-shop__image-link,.bmg-print-shop__card-link');
      if(!anchor||!plainClick(event)||anchor.target==='_blank'||anchor.hasAttribute('download'))return;
      const card=anchor.closest('.bmg-print-shop__card');if(!card)return;
      grid.querySelectorAll('.bmg-print-shop__image').forEach(image=>image.style.removeProperty('view-transition-name'));
      const image=card.querySelector('.bmg-print-shop__image');if(image)image.style.viewTransitionName='bmg-focus-work';
      store('bmg-v2-gallery',{url:location.href,y:scrollY,handle:card.dataset.handle,time:Date.now()});
    });
    let restoreTimer=0,cancelRestore=false;
    const saved=lastGallery();
    const requested=read('bmg-v2-return');
    if(saved&&requested&&Date.now()-requested<60000&&keyForURL(saved.url)===keyForURL(location.href)) {
      remove('bmg-v2-return');
      const deadline=Date.now()+8000;
      const cancel=()=>{cancelRestore=true;clearTimeout(restoreTimer);};
      state.listen(window,'wheel',cancel,{passive:true});state.listen(window,'touchstart',cancel,{passive:true});state.listen(window,'keydown',cancel);
      const restore=()=>{
        if(cancelRestore)return;
        const count=root.querySelector('[data-bmg-count]');
        if((count?.textContent.trim()||Date.now()>deadline)&&document.readyState==='complete') {
          layout();window.scrollTo({top:Number(saved.y)||0,behavior:'instant'});return;
        }
        restoreTimer=setTimeout(restore,80);
      };
      restoreTimer=setTimeout(restore,0);
    }
    state.cleanup(()=>{mutations.disconnect();observer?.disconnect();cancelAnimationFrame(scheduled);clearTimeout(restoreTimer);toolbar.remove();root.removeAttribute('data-v2-view');});
    layout();
  }
  function productSetup(root,state) {
    const handle=decodeURIComponent(location.pathname.split('/products/')[1]?.split('/')[0]||'');
    const saved=lastGallery(handle);
    const image=root.querySelector('.bmg-product-page__hero-image');
    const back=root.querySelector('.bmg-back-link');
    if(saved&&image)image.style.viewTransitionName='bmg-focus-work';
    if(saved&&back) {
      const oldURL=back.getAttribute('href'),oldText=back.textContent;
      back.href=saved.url;back.textContent='← '+(configuration()?.dataset.backLabel||oldText.replace(/^←\s*/,''));
      state.listen(back,'click',event=>{if(plainClick(event))store('bmg-v2-return',Date.now());});
      state.cleanup(()=>{back.setAttribute('href',oldURL);back.textContent=oldText;});
    }
    const art=root.querySelector('[data-bmg-wall-art]');
    if(art&&motion()) {
      let previous=art.dataset.frame,animation;
      const observer=new MutationObserver(()=>{
        const current=art.dataset.frame;if(current===previous)return;previous=current;animation?.cancel();
        animation=state.animate(art,[{opacity:.72},{opacity:1}],{duration:240,easing:'ease-out'});
      });
      observer.observe(art,{attributes:true,attributeFilter:['data-frame']});state.cleanup(()=>observer.disconnect());
    }
  }
  function init() {
    if(!configuration())return;
    document.documentElement.dataset.bmgMotion=motion()?'on':'off';
    document.querySelectorAll('.bmg-home').forEach(root=>mount(root,homeSetup));
    document.querySelectorAll('[data-bmg-v2-journey]').forEach(root=>mount(root,journeySetup));
    document.querySelectorAll('[data-bmg-catalogue]').forEach(root=>mount(root,catalogueSetup));
    document.querySelectorAll('[data-bmg-product-root]').forEach(root=>mount(root,productSetup));
  }
  const restart=()=>{[...instances.values()].forEach(dispose=>dispose());init();};
  preference.addEventListener('change',restart);
  document.addEventListener('shopify:section:load',restart);
  document.addEventListener('shopify:section:unload',event=>{[...instances.entries()].forEach(([root,dispose])=>{if(event.target.contains(root))dispose();});});
  window.addEventListener('pageshow',init);
  window.BMGExperienceV2={version:VERSION,init};
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init);else init();
})();
